import { Component, DestroyRef, ElementRef, NgZone, OnDestroy, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { entityRefs } from '../../agent/agent-catalog';
import { AgentContextService } from '../../agent/agent-context.service';
import { AgentRefreshService } from '../../agent/agent-refresh.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NbDialogService } from '@nebular/theme';
import { ChatSettingsDialogComponent } from '../../components/dialogs/chat-settings-dialog/chat-settings-dialog.component';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { ChatSettingsFormComponent } from '../../components/forms/chat-settings-form/chat-settings-form.component';
import { ORBI_IDLE } from '../../components/orbi-lab/orbi-lab.component';
import {
  ChatBubbleStyle,
  ChatService,
  ChatSettings,
  ChatSettingStyles,
  DEFAULT_CHAT_STYLES,
} from '../../services/chat.service';
import {
  ColorValue,
  parseColorValue,
  solidFallback,
  toCssWithAlpha,
} from '../../components/color-input/color-value';

/** Ablauf der Orbi-Chat-Blase in der Vorschau — derselbe wie
 *  `LAUNCHER_SCRIPT` in chat-widget/…/orbi-launcher.component.ts (dort die
 *  echte Blase auf der Kundenseite). Nur Bewegungen, die unten rechts am
 *  Bildschirmrand Platz haben: nach oben hüpfen, lachen, wackeln. */
export const ORBI_BUBBLE_SCRIPT = [
  ORBI_IDLE, ORBI_IDLE,
  'laugh', ORBI_IDLE, ORBI_IDLE,
  'hop-small', ORBI_IDLE, ORBI_IDLE,
  'giggle-bounce', ORBI_IDLE, ORBI_IDLE, ORBI_IDLE,
  'hop-laugh', ORBI_IDLE, ORBI_IDLE,
  'wiggle', ORBI_IDLE, ORBI_IDLE,
  'hop', ORBI_IDLE, ORBI_IDLE,
];

/** Inline-Styles der Pop-up-Nachricht in der Vorschau. */
export interface PopupPreviewStyles {
  message: Record<string, string>;
  reply: Record<string, string>;
}

/** Pop-up-Nachricht über der Chat-Blase (`app-interactive-question` im
 *  chat-widget) nach denselben Regeln wie dort: Hintergrund als rgba aus
 *  Farbe und Transparenz, Schrift voll deckend; fehlt ein Wert oder ist er
 *  ungültig, gilt der Theme-Wert (Hintergrund `secondaryColor`, Schrift
 *  `botTextColor`, Antwort-Buttons #ffffff / 25 % / #000000).
 *
 *  Hintergründe dürfen ein Verlauf sein (`color-value.ts`): dann trägt jeder
 *  Stopp seine Deckkraft × (1 − Transparenz) als rgba, ausgegeben unter
 *  `background` statt `background-color`. Eine eigene Deckkraft der Farbe
 *  (`#rrggbbaa`) wird ebenso mit der Transparenz multipliziert. */
export function buildPopupPreview(
  styles: Partial<ChatSettingStyles>
): PopupPreviewStyles {
  return {
    message: {
      ...backgroundWithTransparency(
        validColor(styles.popupBackgroundColor) ??
          validColor(styles.secondaryColor) ??
          (parseColorValue(DEFAULT_CHAT_STYLES.secondaryColor) as ColorValue),
        transparency(styles.popupTransparency, 0)
      ),
      color:
        validHex(styles.popupTextColor) ??
        validHex(styles.botTextColor) ??
        DEFAULT_CHAT_STYLES.botTextColor,
    },
    reply: {
      ...backgroundWithTransparency(
        validColor(styles.popupReplyBackgroundColor) ??
          (parseColorValue('#ffffff') as ColorValue),
        transparency(styles.popupReplyTransparency, 25)
      ),
      color: validHex(styles.popupReplyTextColor) ?? '#000000',
    },
  };
}

/** Farben, die die Vorschau als Fläche bzw. als Einzelfarbe zeigt — einmal je
 *  neuem `previewStyles` gebaut. Flächen (`…Fill`) gehen an `background`, damit
 *  Verläufe erscheinen; Schrift/Symbol/Rahmen (`…Ink`) bekommen über
 *  `solidFallback` immer eine einzelne Farbe (beim Verlauf den ersten Stopp). */
export interface PreviewPaint {
  windowFill: string;
  primaryFill: string;
  primaryInk: string;
  secondaryFill: string;
  bubbleSecondaryFill: string;
  botTextInk: string;
  userTextInk: string;
  userBubbleFill: string;
}

export function buildPreviewPaint(styles: Partial<ChatSettingStyles>): PreviewPaint {
  return {
    windowFill: styles.backgroundColor || '#f8f8f8',
    primaryFill: styles.primaryColor || '#2ccb79',
    primaryInk: solidFallback(styles.primaryColor) || '#2ccb79',
    secondaryFill: styles.secondaryColor || '#e8e8e8',
    bubbleSecondaryFill: styles.secondaryColor || '#2ccb79',
    botTextInk: solidFallback(styles.botTextColor) || '#000000',
    userTextInk: solidFallback(styles.userTextColor) || '#000000',
    userBubbleFill: styles.userBubbleColor || '#ffffff',
  };
}

/** '#rgb' / '#rrggbb' / '#rrggbbaa' (das '#' darf fehlen, wie im Widget) →
 *  '#…', sonst undefined. Für Schriftfarben (immer einfarbig). */
function validHex(value: unknown): string | undefined {
  const match =
    typeof value === 'string'
      ? /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value.trim())
      : null;
  return match ? `#${match[1]}` : undefined;
}

/** Einfarbig oder Verlauf nach `color-value.ts`, sonst undefined. */
function validColor(value: unknown): ColorValue | undefined {
  return parseColorValue(value) ?? undefined;
}

/** 0–100, sonst `fallback`. */
function transparency(value: unknown, fallback: number): number {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n)
    ? Math.min(100, Math.max(0, n))
    : fallback;
}

function backgroundWithTransparency(
  color: ColorValue,
  transparencyPercent: number
): Record<string, string> {
  const factor = Math.round(100 - transparencyPercent) / 100;
  const css = toCssWithAlpha(color, factor);
  return color.kind === 'gradient' ? { background: css } : { 'background-color': css };
}

@Component({
  selector: 'app-chat-styling',
  templateUrl: './chat-styling.component.html',
  styleUrl: './chat-styling.component.scss',
})
export class ChatStylingComponent implements OnDestroy {
  selectedChatStyle?: ChatSettings;
  /** Die eigene Liste ist da und ausgewertet. Die Seite hängt daran statt an
   *  `chatService.loading$`: das geht bei jedem Anlegen/Speichern kurz auf
   *  true und nahm Formular und Vorschau hinter dem Namensdialog weg — und
   *  beim Direktaufruf kam die Antwort der Service-eigenen Liste vor der
   *  hiesigen an, sodass Kunden mit Styling kurz das gesperrte
   *  Standard-Formular sahen. */
  loaded = false;
  /** Drives the live preview. Without a selected style (fresh client, or the
   *  last style was deleted) it shows the default styling, so the page always
   *  presents an example widget instead of an empty state. */
  get previewStyles(): Partial<ChatSettingStyles> {
    return this._previewStyles;
  }
  set previewStyles(styles: Partial<ChatSettingStyles>) {
    this._previewStyles = styles;
    this.popupPreview = buildPopupPreview(styles);
    this.paint = buildPreviewPaint(styles);
  }
  private _previewStyles: Partial<ChatSettingStyles> = { ...DEFAULT_CHAT_STYLES };
  /** Styles der Pop-up-Nachricht — einmal je neuem `previewStyles` gebaut,
   *  nicht in jedem Change-Detection-Durchgang. */
  popupPreview: PopupPreviewStyles = buildPopupPreview(this._previewStyles);
  /** Flächen- und Einzelfarben der Vorschau — ebenfalls einmal je Stand. */
  paint: PreviewPaint = buildPreviewPaint(this._previewStyles);
  readonly orbiBubbleScript = ORBI_BUBBLE_SCRIPT;
  /** Reiter rechts — bestimmt, welche Felder die linke Karte zeigt. */
  activeSection: 'window' | 'bubble' | 'aids' = 'window';

  onPreviewTab(tab: { tabId?: string }) {
    this.activeSection = tab?.tabId === 'bubble' || tab?.tabId === 'aids' ? tab.tabId : 'window';
  }

  /** Liquid-Interface gewählt? Dann zeigen die Vorschauen das neue Design —
   *  sonst das Chat-Fenster (auch bei „Automatisch“). 'classic' ist ein
   *  früherer Dot Type des Liquid-Interface und gilt wie 'liquid'. */
  get commandLook(): 'liquid' | null {
    const design = this.previewStyles.widgetDesign;
    return design === 'classic' || design === 'liquid' ? 'liquid' : null;
  }

  /** Interface-Typ oben auf der Seite: Liquid (links) | Chat (rechts);
   *  null = noch nicht gewählt (je Agent automatisch). */
  get interfaceType(): 'liquid' | 'chat' | null {
    const design = this.previewStyles.widgetDesign;
    if (design === 'window') return 'chat';
    return design === 'classic' || design === 'liquid' ? 'liquid' : null;
  }

  private readonly translate = inject(TranslateService);
  readonly interfaceOptions = [
    { value: 'liquid', label: this.translate.instant('pages.chatStyling.interfaceType.liquid') },
    { value: 'chat', label: this.translate.instant('pages.chatStyling.interfaceType.chat') },
  ];

  /** Schreibt über das Formular (speichert wie jedes Feld). Ohne eigenes
   *  Styling ist das Standard-Design gesperrt — wie jede andere Eingabe
   *  öffnet der Klick dann den Namensdialog. */
  onInterfaceType(type: string) {
    if (!this.selectedChatStyle || !this.settingsForm) {
      this.onAdd();
      return;
    }
    this.settingsForm.setInterface(type === 'chat' ? 'classic' : 'liquid');
  }

  /** Effective bubble style for the live preview. Falls back to the legacy
   *  `enableChatBubbleAnimation` boolean when an existing style record
   *  doesn't yet carry the new `chatBubbleStyle` field. */
  get bubbleStylePreview(): ChatBubbleStyle {
    const style = this.previewStyles.chatBubbleStyle;
    if (style) return style;
    if (this.previewStyles.enableChatBubbleAnimation === false) return 'dot';
    return 'blob';
  }

  /** Ohne eigenes Chat-Styling zeigt die linke Spalte das Formular mit dem
   *  Standard-Design — zum Ansehen, was sich alles einstellen lässt, nicht zum
   *  Ändern (Marcel, 28.09.2026). Jede Eingabe dort (Klick, Tippen, Einfügen,
   *  Ablegen) wird am Formularrahmen in der Capture-Phase abgefangen, bevor
   *  ein Feld oder Nebular sie sieht, und öffnet stattdessen den Namensdialog;
   *  editierbar ist erst das angelegte Styling. Hover (Hilfe-Popover), Tab und
   *  Tastenkürzel des Browsers (Zoom, Suchen …) bleiben frei. Was trotzdem
   *  durchkommt (Schriftliste im Overlay, Touch am Schieberegler, IME), fängt
   *  das Formular selbst ab: `[locked]` setzt es zurück und meldet
   *  `lockedEdit`. */
  @ViewChild(ChatSettingsFormComponent) private settingsForm?: ChatSettingsFormComponent;

  @ViewChildren('settingsFormHost')
  set settingsFormHosts(refs: QueryList<ElementRef<HTMLElement>> | undefined) {
    this.bindDefaultGate(refs?.map((ref) => ref.nativeElement) ?? []);
  }

  private static readonly GATE_EVENTS = [
    'pointerdown',
    'mousedown',
    'click',
    'keydown',
    'beforeinput',
    'paste',
    'drop',
  ];
  /** Tasten, die keinen Wert ändern — Fokus wandern lassen bleibt erlaubt.
   *  Kürzel mit Strg/Cmd/Alt laufen ebenfalls durch (Zoom, Suchen, Neu
   *  laden); Einfügen und Ausschneiden kommen als `paste`/`beforeinput`. */
  private static readonly GATE_PASS_KEYS = new Set([
    'Tab',
    'Escape',
    'Shift',
    'Control',
    'Alt',
    'Meta',
    'CapsLock',
    'ContextMenu',
    'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
  ]);
  private unbindDefaultGate?: () => void;
  private nameDialogOpen = false;

  constructor(
    protected chatService: ChatService,
    protected dialogService: NbDialogService,
    protected router: Router,
    private ngZone: NgZone
  ) {
    // "this styling" for the in-house agent = the one open on the page.
    inject(AgentContextService).report(
      this,
      () => ({ selection: entityRefs('chat_styling', [this.selectedChatStyle?.id]) }),
      inject(DestroyRef)
    );
    // The in-house agent changed a styling: reload and keep the open one selected.
    inject(AgentRefreshService)
      .on('chat_styling')
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.chatService.listChatSettings().subscribe((settings) => {
          const current = settings.find((s) => s.id === this.selectedChatStyle?.id);
          this.onSelect(current ?? settings[0]);
          // Same styling stays open, so the form sees no new id and would keep its
          // old values — and its auto-save writes ALL of them back on the next edit,
          // silently undoing the agent's change. Make it re-read.
          if (current) this.settingsForm?.updateChatSettings(current.id);
        })
      );

    this.chatService.listChatSettings().subscribe({
      next: (settings) => {
        // when there is bucketId in url query, select the bucket
        const chatStyleId = this.router.parseUrl(this.router.url).queryParams[
          'chatStyleId'
        ];
        const chatStyle = this.chatService.chatSettings$.value.find(
          (b) => b.id === chatStyleId
        );

        if (chatStyleId && chatStyle) {
          this.onSelect(chatStyle);
        } else {
          this.onSelect(settings[0]);
        }
        this.loaded = true;
      },
      // Ohne Liste bleibt das Standard-Design stehen, statt einer leeren Seite.
      error: () => (this.loaded = true),
    });
  }

  onStylesChange(styles: Partial<ChatSettingStyles>) {
    this.previewStyles = styles;
  }

  onSelect(chatStyle?: ChatSettings) {
    if (!chatStyle?.id) {
      this.selectedChatStyle = undefined;
      this.previewStyles = { ...DEFAULT_CHAT_STYLES };
      return;
    }

    // add bucket id to url query
    this.router.navigate([], {
      queryParams: { chatStyleId: chatStyle?.id },
      queryParamsHandling: 'merge',
    });

    this.selectedChatStyle = chatStyle;
    this.previewStyles = chatStyle.styles ? { ...chatStyle.styles } : {};
  }

  onSelectById(id: string) {
    const style = this.chatService.chatSettings$.value.find((s) => s.id === id);
    if (style) this.onSelect(style);
  }

  onAdd(chatStyle?: ChatSettings) {
    // Nie zwei Namensdialoge übereinander: Maus- und Tastaturereignisse einer
    // Geste am gesperrten Formular kommen mehrfach an.
    if (this.nameDialogOpen) return;
    this.nameDialogOpen = true;
    const ref = this.dialogService.open(ChatSettingsDialogComponent, {
      context: {
        data: chatStyle,
        onComplete: (chatSettings: ChatSettings) => {
          // Erst nach dem Rendern ihrer nb-option wählen (#186): nb-select
          // merkt sich einen Wert nur, solange es gar keine Optionen hat —
          // das neue Styling bekam seine Option im selben Durchlauf wie
          // `[selected]`, der Wert fiel weg und das Feld blieb leer.
          setTimeout(() => this.onSelect(chatSettings));
        },
      },
    });
    ref.onClose.subscribe(() => (this.nameDialogOpen = false));
  }

  ngOnDestroy() {
    this.unbindDefaultGate?.();
  }

  private bindDefaultGate(hosts: HTMLElement[]) {
    this.unbindDefaultGate?.();
    this.unbindDefaultGate = undefined;
    if (!hosts.length) return;

    const onEvent = (event: Event) => {
      if (this.selectedChatStyle) return;
      if (
        event instanceof KeyboardEvent &&
        (ChatStylingComponent.GATE_PASS_KEYS.has(event.key) ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey)
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // pointerdown/mousedown halten das Feld nur zurück (kein Fokus, kein
      // Schieberegler-Ziehen); der Dialog öffnet auf den Klick selbst.
      if (event.type === 'pointerdown' || event.type === 'mousedown') return;
      this.ngZone.run(() => this.onAdd());
    };

    this.ngZone.runOutsideAngular(() => {
      hosts.forEach((host) =>
        ChatStylingComponent.GATE_EVENTS.forEach((type) =>
          host.addEventListener(type, onEvent, true)
        )
      );
    });
    this.unbindDefaultGate = () =>
      hosts.forEach((host) =>
        ChatStylingComponent.GATE_EVENTS.forEach((type) =>
          host.removeEventListener(type, onEvent, true)
        )
      );
  }

  onDelete(chatStyle: ChatSettings, event: MouseEvent) {
    event.stopPropagation();
    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
    });

    // Delete the project if the user confirms
    confirmDialogRef.onClose.subscribe(async (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.chatService.deleteChatSettings(chatStyle.id).subscribe(() => {
          if (chatStyle.id === this.selectedChatStyle?.id) {
            // Falls back to the default-styling preview when it was the last one.
            this.onSelect(this.chatService.chatSettings$.value?.[0]);
          }
        });
      }
    });
  }
}
